/** The /faq page (and the old /help URL). Facts come from settings via src/shop-faq.ts + resolveContact. */
import { useEffect, useMemo } from "react";
import { resolveContact } from "../../../src/shop-contact";
import { buildFaq, faqContactLines, type FaqSeg } from "../../../src/shop-faq";
import { Go, pathFor, type GoFn } from "./route";
import { setSeo } from "./seo";
import type { ShopConfig } from "./types";

const EXT = { target: "_blank", rel: "noopener noreferrer" } as const;

export function FaqPage({ config, go }: { config: ShopConfig | null; go: GoFn }) {
  const r = useMemo(() => resolveContact(config), [config]);
  const groups = useMemo(() => buildFaq(r), [r]);
  useEffect(() => {
    /** /help is the old URL of this page: settle on /faq so the canonical, the share link and Back agree. */
    if (/\/help\/?$/.test(window.location.pathname)) window.history.replaceState({}, "", pathFor({ name: "faq" }));
    setSeo("FAQ", "Ordering, payment, delivery, collection, returns, wholesale accounts and deals at Z S MERCH.");
  }, []);
  const seg = (s: FaqSeg, i: number) =>
    typeof s === "string" ? s
      : "href" in s ? <a key={i} href={s.href} {...(/^https?:/.test(s.href) ? EXT : {})}>{s.text}</a>
        : <Go key={i} to={s.to === "signup" ? { name: "account", tab: "up" } : s.to === "clearance" ? { name: "clearance" } : { name: "contact" }} go={go}>{s.text}</Go>;
  return (
    <div className="wrap page-pad faq">
      <nav className="crumbs" aria-label="Breadcrumb"><ol><li><Go to={{ name: "home" }} go={go}>Home</Go></li><li aria-current="page">FAQ</li></ol></nav>
      <h1>Frequently asked questions</h1>
      {groups.map((g) => (
        <section key={g.id} aria-labelledby={`faq-${g.id}`}>
          <h2 id={`faq-${g.id}`}>{g.title}</h2>
          {g.items.map((i) => (
            <details key={i.id} className="faq-item">
              <summary>{i.q}</summary>
              <div className="faq-a">{i.a.map((p, n) => <p key={n}>{p.map(seg)}</p>)}</div>
            </details>
          ))}
        </section>
      ))}
      <section aria-labelledby="faq-help">
        <h2 id="faq-help">Still need help?</h2>
        <ul className="faq-contact">
          {faqContactLines(r).map((l) => <li key={l.href}><a href={l.href} {...(/^https?:/.test(l.href) ? EXT : {})}>{l.label}</a></li>)}
        </ul>
      </section>
    </div>
  );
}
